import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Task, TaskStatus, TaskPriority, TaskType, ChecklistItem, Comment, Dependency, DependencyType, Project, Collection, CONSTRAINT_TYPE_LABEL } from '../types';
import { getProject } from '../services/projectService';
import { dvUpdate } from '../api/dataverseClient';
import {
  getSubtasks, createSubtask, getProjectTasks, getProjectCollections,
  getChecklistItems, createChecklistItem, updateChecklistItem, deleteChecklistItem,
  deleteTask, updateTaskCollection,
} from '../services/taskService';
import BoardModal, { BMCancelButton, BMDangerButton } from '../components/BoardModal';
import { darkenHex } from '../board/BoardView';
import { getTaskComments, createTaskComment } from '../services/commentService';
import { getEntityActivity, logActivity, ActivityEntityType } from '../services/activityService';
import {
  sendMentionNotification, sendWatcherNotification,
  getMyWatcher, addWatcher, removeWatcher,
} from '../services/notificationService';
import { ActivityLogEntry } from '../types';
import { graphGet } from '../api/graphClient';
import {
  getTaskDependencies, createDependency, deleteDependency,
  DEPENDENCY_TYPE_LABEL, DEPENDENCY_TYPE_SHORT,
} from '../services/dependencyService';
import { usePinState, getMyOid, PinKey } from './cardPinState';
import { getAccount } from '../auth/authService';
import PeoplePicker, { Person } from '../components/PeoplePicker';
import AutoSizingTextarea from '../components/AutoSizingTextarea';
import TagSection from '../components/TagSection';
import { TASK_TAG_POOL } from '../services/tagService';
import { LogTimeModal } from '../views/project/tabs/TimeTab';
import { useCurrentUser } from '../hooks/useCurrentUser';
import EntityApprovalSection from '../components/approvals/EntityApprovalSection';
import { ApprovalEntityType } from '../services/approvalService';
import RecurrencePane from './RecurrencePane';
import CustomFieldsPane from './CustomFieldsPane';
import { getProjectLibraries } from '../services/projectLibraryService';
import {
  DriveItem, listDriveFolder, uploadDriveFile, deleteDriveItem,
  getOrCreateFolderPath, getDownloadUrl,
} from '../services/sharepointService';

interface Props {
  task: Task | null;
  pca?: IPublicClientApplication;
  onClose: () => void;
  onSaved?: (updated: Task) => void;
  /** Called after a task is deleted so the parent view can drop it from state. */
  onDeleted?: (taskId: string) => void;
}

// Watch toggle icon — an inline SVG eye matching the app's icon language
// (stroked outlines, no emoji). Watching = filled brand eye; not watching =
// plain stroked eye.
function WatchEye({ watching }: { watching: boolean }) {
  return (
    <svg width={14} height={14} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M1.5 8S3.8 3.5 8 3.5 14.5 8 14.5 8 12.2 12.5 8 12.5 1.5 8 1.5 8Z"
        stroke="currentColor" strokeWidth={1.3} strokeLinejoin="round"
        fill={watching ? 'currentColor' : 'none'} fillOpacity={watching ? 0.15 : 0}
      />
      <circle cx={8} cy={8} r={2} fill={watching ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.3} />
    </svg>
  );
}

const STATUS_LABEL: Record<number, string> = {
  [TaskStatus.NotStarted]: 'Not Started',
  [TaskStatus.InProgress]: 'In Progress',
  [TaskStatus.InReview]:   'In Review',
  [TaskStatus.Done]:       'Done',
  [TaskStatus.OnHold]:     'On Hold',
  [TaskStatus.Cancelled]:  'Cancelled',
};
const STATUS_VALUES = [
  TaskStatus.NotStarted, TaskStatus.InProgress, TaskStatus.InReview,
  TaskStatus.Done, TaskStatus.OnHold, TaskStatus.Cancelled,
];

const PRIORITY_LABEL: Record<number, string> = {
  [TaskPriority.Critical]: 'Critical',
  [TaskPriority.High]:     'High',
  [TaskPriority.Medium]:   'Medium',
  [TaskPriority.Low]:      'Low',
};
const PRIORITY_COLOR: Record<number, string> = {
  [TaskPriority.Critical]: '#DC2626',
  [TaskPriority.High]:     '#EA580C',
  [TaskPriority.Medium]:   '#D97706',
  [TaskPriority.Low]:      '#9198A8',
};
const PRIORITY_VALUES = [
  TaskPriority.Critical, TaskPriority.High, TaskPriority.Medium, TaskPriority.Low,
];

const AVATAR_COLORS = ['#0E7490', '#7C3AED', '#B45309', '#065F46', '#9D174D'];

function initials(name?: string | null): string {
  if (!name) return '?';
  return name.split(/\s+/).slice(0, 2).map(s => s[0] ?? '').join('').toUpperCase();
}
function avatarColor(name?: string | null): string {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function Avatar({ name, size = 22 }: { name?: string | null; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: avatarColor(name), color: '#fff',
      fontSize: size <= 22 ? 10 : 11, fontWeight: 600,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>{initials(name)}</div>
  );
}

function relativeTime(iso: string): string {
  const diff = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'just now';
  const m = Math.round(diff / 60);  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);     if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);     if (d < 7)  return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const SECTION_HEADER: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  fontSize: 11, fontWeight: 600, color: 'var(--ink-3)',
  textTransform: 'uppercase', letterSpacing: '0.12em',
  fontFamily: 'var(--font-ui)',
  cursor: 'pointer', userSelect: 'none',
  padding: '12px 0 0',
  borderTop: '0.5px solid var(--border-light)',
  marginTop: 16,
};

function Section({ title, defaultOpen = true, rightSlot, children }: { title: string; defaultOpen?: boolean; rightSlot?: React.ReactNode; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <div style={SECTION_HEADER} onClick={() => setOpen(o => !o)}>
        <span>{title}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          {rightSlot}
          <span style={{ fontSize: 10, color: 'var(--ink-4)' }}>{open ? '▾' : '▸'}</span>
        </span>
      </div>
      {open && <div style={{ paddingTop: 12 }}>{children}</div>}
    </div>
  );
}

// Pin-to-card checkbox — shares state with TaskCard via cardPinState store.
function PinToCardCheckbox({ pinKey, taskId, userId, pca }: { pinKey: PinKey; taskId: string; userId: string; pca: IPublicClientApplication }) {
  const [state, toggle] = usePinState(userId, taskId, pca);
  const checked = state[pinKey];
  return (
    <label
      onClick={e => e.stopPropagation()}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 10, fontWeight: 500, color: checked ? 'var(--brand)' : 'var(--ink-4)',
        textTransform: 'none', letterSpacing: 0,
        cursor: 'pointer', userSelect: 'none',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={e => toggle(pinKey, e.target.checked)}
        style={{ width: 12, height: 12, cursor: 'pointer', margin: 0 }}
      />
      Pin to card
    </label>
  );
}

// ── Main component ────────────────────────────────────────────────────
export default function TaskDetailDrawer({ task, pca, onClose, onSaved, onDeleted }: Props) {
  const [draft, setDraft] = useState<Task | null>(task);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nestedTask, setNestedTask] = useState<Task | null>(null);
  const [showLogTime, setShowLogTime] = useState(false);
  const [project, setProject] = useState<Project | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [collections, setCollections] = useState<Collection[]>([]);
  // Watch toggle state: null = still loading the watcher row; otherwise
  // the watcher's GUID when this user is watching, or '' for not watching.
  // Tri-state lets us disable the button while the initial query is in
  // flight so a fast toggle can't create a duplicate row.
  const [watcherId, setWatcherId] = useState<string | null | ''>(null);
  const [watcherBusy, setWatcherBusy] = useState(false);
  const flashTimer = useRef<number | null>(null);
  const textDebounce = useRef<number | null>(null);
  const dirtyRef = useRef(false);
  const me = useCurrentUser(pca, task?._dagrun_pm_projectid_value ?? '');
  const [approvalGate, setApprovalGate] = useState({ hasChain: false, requireCompletion: false, allApproved: false });
  const [statusBlockMsg, setStatusBlockMsg] = useState<string | null>(null);

  useEffect(() => {
    setDraft(task);
    setError(null);
    setNestedTask(null);
    setMenuOpen(false);
    setShowDeleteConfirm(false);
    dirtyRef.current = false;
  }, [task]);

  // Collections for the drawer's Collection selector — load per project.
  useEffect(() => {
    const pid = task?._dagrun_pm_projectid_value;
    if (!pid || !pca) { setCollections([]); return; }
    let cancelled = false;
    getProjectCollections(pca, pid)
      .then(cs => { if (!cancelled) setCollections(cs); })
      .catch(() => { if (!cancelled) setCollections([]); });
    return () => { cancelled = true; };
  }, [pca, task?._dagrun_pm_projectid_value]);

  // Close the header overflow menu on any outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  // Fetch project lazily so we can gate Log Time on dagrun_pm_module_timetracking.
  useEffect(() => {
    const pid = task?._dagrun_pm_projectid_value;
    if (!pid || !pca) { setProject(null); return; }
    let cancelled = false;
    getProject(pca, pid)
      .then(p => { if (!cancelled) setProject(p); })
      .catch(() => { if (!cancelled) setProject(null); });
    return () => { cancelled = true; };
  }, [pca, task?._dagrun_pm_projectid_value]);

  // Resolve the user's watcher status for this task on open / task switch.
  // Re-runs when me.oid resolves so we don't query with an empty userId.
  useEffect(() => {
    const tid = task?.dagrun_pm_taskid;
    const oid = me?.oid;
    if (!tid || !oid || !pca) { setWatcherId(null); return; }
    let cancelled = false;
    setWatcherId(null);
    getMyWatcher(pca, 'Task', tid, oid)
      .then(id => { if (!cancelled) setWatcherId(id ?? ''); })
      .catch(() => { if (!cancelled) setWatcherId(''); });
    return () => { cancelled = true; };
  }, [pca, task?.dagrun_pm_taskid, me?.oid]);

  const toggleWatch = async () => {
    if (!pca || !me?.oid || watcherBusy || watcherId === null) return;
    const tid = task?.dagrun_pm_taskid;
    if (!tid) return;
    setWatcherBusy(true);
    try {
      if (watcherId === '') {
        const newId = await addWatcher(pca, 'Task', tid, me.oid, me.displayName);
        setWatcherId(newId);
      } else {
        await removeWatcher(pca, watcherId);
        setWatcherId('');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Watch toggle failed');
    } finally {
      setWatcherBusy(false);
    }
  };

  if (!task || !draft || !pca) return null;

  const handleClose = () => {
    if (dirtyRef.current) onSaved?.(draft);
    onClose();
  };

  const patch = async (fields: Partial<Task>) => {
    try {
      await dvUpdate(pca, 'dagrun_pm_tasks', task.dagrun_pm_taskid, fields as Record<string, unknown>);
      setError(null);
      dirtyRef.current = true;
      setSavedFlash(true);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const handleDelete = async () => {
    if (!pca || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteTask(pca, task);
      onDeleted?.(task.dagrun_pm_taskid);
      setShowDeleteConfirm(false);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  // Move the task to another collection (or clear it). Optimistic + patches the
  // wip-collection lookup; marks dirty so the board/list reflect the move on close.
  const handleCollectionChange = (collectionId: string) => {
    const next = collectionId || null;
    if (next === (draft._dagrun_pm_wipcollectionid_value ?? null)) return;
    setDraft(d => (d ? { ...d, _dagrun_pm_wipcollectionid_value: next } : d));
    dirtyRef.current = true;
    updateTaskCollection(pca, task.dagrun_pm_taskid, next)
      .then(() => {
        setSavedFlash(true);
        if (flashTimer.current) window.clearTimeout(flashTimer.current);
        flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to move collection'));
  };

  const setAndPatch = <K extends keyof Task>(k: K, v: Task[K]) => {
    // Log activity before updating the draft so `draft` still holds the
    // OLD value when we need it for the "X → Y" message. Fire-and-forget.
    maybeLogActivity(k, draft[k], v);
    setDraft(d => (d ? { ...d, [k]: v } : d));
    patch({ [k]: v } as Partial<Task>);
  };

  // Require-completion gate: an approval chain with "require completion" on
  // blocks the Done transition until every stage is approved.
  const onStatusChange = (next: number) => {
    if (next === TaskStatus.Done && approvalGate.requireCompletion && !approvalGate.allApproved) {
      setStatusBlockMsg('This task requires full approval before it can be marked Done.');
      return;
    }
    setStatusBlockMsg(null);
    setAndPatch('dagrun_pm_status', next);
  };

  const maybeLogActivity = <K extends keyof Task>(k: K, prev: Task[K], next: Task[K]) => {
    if (prev === next || !task._dagrun_pm_projectid_value) return;
    const common = {
      projectId: task._dagrun_pm_projectid_value,
      entityType: ActivityEntityType.Task,
      entityId: task.dagrun_pm_taskid,
      entityTitle: task.dagrun_pm_name,
      actorId: me?.oid,
      actorName: me?.displayName,
    };
    if (k === 'dagrun_pm_status') {
      const fromLabel = STATUS_LABEL[Number(prev)] ?? '?';
      const toLabel = STATUS_LABEL[Number(next)] ?? '?';
      logActivity(pca, { ...common, action: 'StatusChanged', description: `${fromLabel} → ${toLabel}` });
      // Notify watchers (excluding actor) of the status flip.
      if (me) {
        sendWatcherNotification(pca, {
          entityType: 'Task',
          entityId: task.dagrun_pm_taskid,
          entityTitle: task.dagrun_pm_name,
          projectId: common.projectId,
          projectName: project?.dagrun_pm_name ?? '',
          changeDescription: `${me.displayName} changed status to ${toLabel}`,
          actorId: me.oid,
          actorName: me.displayName,
          notificationType: 'StatusChange',
          activityType: 'statusChanged',
        }).catch(err => console.warn('sendWatcherNotification (status) failed:', err));
      }
    } else if (k === 'dagrun_pm_priority') {
      logActivity(pca, { ...common, action: 'PriorityChanged', description: `${PRIORITY_LABEL[Number(prev)] ?? 'None'} → ${PRIORITY_LABEL[Number(next)] ?? 'None'}` });
    } else if (k === 'dagrun_pm_duedate') {
      const p = prev ? new Date(String(prev)).toLocaleDateString() : '—';
      const n = next ? new Date(String(next)).toLocaleDateString() : '—';
      logActivity(pca, { ...common, action: 'DueDateChanged', description: `${p} → ${n}` });
    } else if (k === 'dagrun_pm_percentcomplete') {
      logActivity(pca, { ...common, action: 'PercentCompleteChanged', description: `${prev ?? 0}% → ${next ?? 0}%` });
    }
  };

  const setTextDebounced = <K extends keyof Task>(k: K, v: Task[K]) => {
    setDraft(d => (d ? { ...d, [k]: v } : d));
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    textDebounce.current = window.setTimeout(() => patch({ [k]: v } as Partial<Task>), 600);
  };

  const flushText = <K extends keyof Task>(k: K, v: Task[K]) => {
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    patch({ [k]: v } as Partial<Task>);
  };

  const setNumberChange = <K extends keyof Task>(k: K, raw: string) => {
    const num = raw === '' ? null : Number(raw);
    setDraft(d => (d ? { ...d, [k]: num as Task[K] } : d));
  };

  const setNumberOnBlur = <K extends keyof Task>(k: K, raw: string) => {
    const num = raw === '' ? null : Number(raw);
    const fields: Partial<Task> = { [k]: num as Task[K] };
    // Bidirectional pct ↔ effort sync, when estimated hours is set.
    // If estimated is not set: fields are independent.
    if (k === 'dagrun_pm_effortcompletedhours') {
      const est = draft.dagrun_pm_estimatedhours;
      if (est && est > 0) {
        fields.dagrun_pm_percentcomplete = Math.min(100, Math.round(((num ?? 0) / est) * 100));
      }
    } else if (k === 'dagrun_pm_percentcomplete') {
      const est = draft.dagrun_pm_estimatedhours;
      if (est && est > 0) {
        const pct = num ?? 0;
        // Round to 2 decimal places.
        fields.dagrun_pm_effortcompletedhours = Math.round(((est * pct) / 100) * 100) / 100;
      }
    } else if (k === 'dagrun_pm_estimatedhours') {
      // Preserve legacy behavior: when estimated changes, re-derive pct from
      // current effort so the relationship stays consistent.
      const est = num as number | null;
      const done = draft.dagrun_pm_effortcompletedhours;
      if (est && est > 0) {
        fields.dagrun_pm_percentcomplete = Math.min(100, Math.round((done / est) * 100));
      }
    }
    setDraft(d => (d ? { ...d, ...fields } : d));
    patch(fields);
  };

  const assigneePerson: Person | null = draft.dagrun_pm_assigneeid
    ? { aadObjectId: draft.dagrun_pm_assigneeid, displayName: draft.dagrun_pm_assigneename ?? draft.dagrun_pm_assigneeid }
    : null;

  const handleAssigneeChange = (p: Person | null) => {
    const prevId = draft.dagrun_pm_assigneeid;
    const prevName = draft.dagrun_pm_assigneename;
    const nextId = p?.aadObjectId ?? null;
    const nextName = p?.displayName ?? null;
    const fields: Partial<Task> = { dagrun_pm_assigneeid: nextId, dagrun_pm_assigneename: nextName };
    if (task._dagrun_pm_projectid_value && prevName !== nextName) {
      logActivity(pca, {
        projectId: task._dagrun_pm_projectid_value,
        entityType: ActivityEntityType.Task,
        entityId: task.dagrun_pm_taskid,
        entityTitle: task.dagrun_pm_name,
        actorId: me?.oid,
        actorName: me?.displayName,
        action: p ? 'Assigned' : 'Unassigned',
        description: p ? (nextName ?? 'a user') : (prevName ?? 'previous assignee'),
      });
    }
    setDraft(d => (d ? { ...d, ...fields } : d));
    patch(fields);

    // Notify the new assignee — only when the assignee actually changed
    // and a non-null new assignee exists. recipientOverride bypasses the
    // watcher query because the recipient is the new assignee, not a
    // generic watcher.
    const projectId = task._dagrun_pm_projectid_value;
    if (projectId && nextId && nextId !== prevId && me) {
      sendWatcherNotification(pca, {
        entityType: 'Task',
        entityId: task.dagrun_pm_taskid,
        entityTitle: task.dagrun_pm_name,
        projectId,
        projectName: project?.dagrun_pm_name ?? '',
        changeDescription: `${me.displayName} assigned this task to you`,
        actorId: me.oid,
        actorName: me.displayName,
        notificationType: 'Assignment',
        activityType: 'taskAssigned',
        recipientOverride: [{ userId: nextId, userName: nextName ?? 'Assignee' }],
      }).catch(err => console.warn('sendWatcherNotification (assign) failed:', err));
    }
  };

  return (
    <>
      <div onClick={handleClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.18)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 480,
        background: 'var(--surface)', borderLeft: '0.5px solid var(--border-light)',
        boxShadow: 'var(--shadow-panel)', zIndex: 101,
        overflowY: 'auto', fontFamily: 'var(--font-ui)',
        padding: '16px 24px 32px 24px',
      }}>
        {/* HEADER */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: error ? 'var(--danger)' : 'var(--ink-4)', opacity: error || savedFlash ? 1 : 0, transition: 'opacity 0.2s' }}>
            {error ?? (savedFlash ? 'Saved ✓' : '')}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              type="button"
              onClick={toggleWatch}
              disabled={watcherId === null || watcherBusy || !me?.oid}
              aria-label={watcherId ? 'Stop watching' : 'Watch'}
              title={watcherId ? 'Watching — click to stop' : 'Watch this task'}
              style={{
                height: 28, padding: '0 10px',
                display: 'flex', alignItems: 'center', gap: 6,
                background: watcherId ? 'var(--brand-mist)' : 'transparent',
                color: watcherId ? 'var(--brand)' : 'var(--ink-3)',
                border: watcherId ? '1px solid var(--brand)' : '1px solid var(--border)',
                borderRadius: 'var(--r-md)',
                fontFamily: 'var(--font-ui)', fontSize: 12,
                fontWeight: watcherId ? 600 : 500,
                cursor: watcherId === null || watcherBusy ? 'default' : 'pointer',
                opacity: watcherId === null || !me?.oid ? 0.55 : 1,
                transition: 'background 0.12s, color 0.12s, border-color 0.12s',
              }}
            >
              <WatchEye watching={!!watcherId} />
              <span>{watcherId === null ? '…' : (watcherId ? 'Watching' : 'Watch')}</span>
            </button>
            <div style={{ position: 'relative' }} onMouseDown={e => e.stopPropagation()}>
              <button
                type="button"
                aria-label="Task actions"
                title="Task actions"
                onClick={e => { e.stopPropagation(); setMenuOpen(o => !o); }}
                className="btn-ghost"
                style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, lineHeight: 1 }}
              >⋮</button>
              {menuOpen && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 4,
                  background: '#fff', border: '1px solid #DDE0E6', borderRadius: 8,
                  boxShadow: '0 4px 16px rgba(15,17,23,0.12)', minWidth: 160, zIndex: 60, padding: 4,
                }}>
                  <div
                    onClick={() => { setMenuOpen(false); setShowDeleteConfirm(true); }}
                    style={{ padding: '7px 10px', fontSize: 13, color: '#DC2626', cursor: 'pointer', borderRadius: 6 }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#FEF2F2')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >Delete task</div>
                </div>
              )}
            </div>
            <button onClick={handleClose} aria-label="Close" className="btn-ghost" style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>×</button>
          </div>
        </div>

        {/* Task-type discriminator — quiet for plain Tasks, badge for
            Story/Subtask, mirroring the card treatment. */}
        {(draft.dagrun_pm_tasktype === TaskType.Story || draft.dagrun_pm_tasktype === TaskType.Subtask) && (
          <div style={{ marginBottom: 4 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              padding: '2px 8px', borderRadius: 4,
              background: draft.dagrun_pm_tasktype === TaskType.Story ? '#F3E8FF' : '#F3F4F6',
              color: draft.dagrun_pm_tasktype === TaskType.Story ? '#7E22CE' : '#5A6272',
            }}>{draft.dagrun_pm_tasktype === TaskType.Story ? 'Story' : 'Subtask'}</span>
          </div>
        )}

        {/* Title — large, editable */}
        <input
          value={draft.dagrun_pm_name}
          onChange={e => setTextDebounced('dagrun_pm_name', e.target.value)}
          onBlur={e => flushText('dagrun_pm_name', e.target.value)}
          style={{ fontFamily: 'var(--font-ui)', fontSize: 18, fontWeight: 600, color: 'var(--ink)', border: 'none', padding: 0, marginBottom: 16, width: '100%', background: 'transparent' }}
        />

        {/* SECTION: Details (always visible) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label>Status</label>
            <select
              value={draft.dagrun_pm_status}
              onChange={e => onStatusChange(Number(e.target.value))}
            >
              {STATUS_VALUES.map(v => <option key={v} value={v}>{STATUS_LABEL[v]}</option>)}
            </select>
            {statusBlockMsg && <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>{statusBlockMsg}</div>}
          </div>
          <div>
            <label>Priority</label>
            <select
              value={draft.dagrun_pm_priority ?? ''}
              onChange={e => setAndPatch('dagrun_pm_priority', e.target.value === '' ? (null as unknown as number) : Number(e.target.value))}
            >
              <option value="">None</option>
              {PRIORITY_VALUES.map(v => <option key={v} value={v}>{PRIORITY_LABEL[v]}</option>)}
            </select>
          </div>
          {collections.length > 0 && (
            <div style={{ gridColumn: '1 / span 2' }}>
              <label>Collection</label>
              <select
                value={draft._dagrun_pm_wipcollectionid_value ?? ''}
                onChange={e => handleCollectionChange(e.target.value)}
              >
                <option value="">— No collection —</option>
                {collections.map(c => (
                  <option key={c.dagrun_pm_collectionid} value={c.dagrun_pm_collectionid}>
                    {c.dagrun_pm_name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div style={{ gridColumn: '1 / span 2' }}>
            <label>Type</label>
            <select
              value={draft.dagrun_pm_tasktype ?? TaskType.Task}
              onChange={e => setAndPatch('dagrun_pm_tasktype', Number(e.target.value))}
            >
              <option value={TaskType.Task}>Task</option>
              <option value={TaskType.Story}>Story</option>
              {/* Subtask is system-assigned via the Subtasks tab; keep it
                  selectable only when the task already is one so the select
                  never shows a value it doesn't contain. */}
              {draft.dagrun_pm_tasktype === TaskType.Subtask && (
                <option value={TaskType.Subtask}>Subtask</option>
              )}
            </select>
          </div>
          <div>
            <label>Start date</label>
            <input type="date" value={draft.dagrun_pm_startdate?.slice(0, 10) ?? ''} onChange={e => setAndPatch('dagrun_pm_startdate', e.target.value || null)} />
          </div>
          <div>
            <label>Due date</label>
            <input type="date" value={draft.dagrun_pm_duedate?.slice(0, 10) ?? ''} onChange={e => setAndPatch('dagrun_pm_duedate', e.target.value || null)} />
          </div>
          {/* Scheduling constraint (imported from MS Project) — read-only badge
              next to the dates it constrains. Hidden for None (ASAP). */}
          {draft.dagrun_pm_constrainttype != null && draft.dagrun_pm_constrainttype !== 100000000 && (
            <div style={{ gridColumn: '1 / span 2', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '3px 10px', borderRadius: 10,
                background: 'var(--brand-mist)', color: 'var(--brand)',
                fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-ui)',
              }}>
                ⚓ {CONSTRAINT_TYPE_LABEL[draft.dagrun_pm_constrainttype] ?? 'Constraint'}
                {draft.dagrun_pm_constraintdate && (
                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>
                    {new Date(draft.dagrun_pm_constraintdate).toLocaleDateString()}
                  </span>
                )}
              </span>
              <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>Imported scheduling constraint</span>
            </div>
          )}
          <div style={{ gridColumn: '1 / span 2' }}>
            <label>Assignee</label>
            <PeoplePicker pca={pca} value={assigneePerson} onChange={handleAssigneeChange} placeholder="Assign to…" />
          </div>
          <div>
            <label>Estimated Effort</label>
            <input type="number" step="0.25" value={draft.dagrun_pm_estimatedhours ?? ''} onChange={e => setNumberChange('dagrun_pm_estimatedhours', e.target.value)} onBlur={e => setNumberOnBlur('dagrun_pm_estimatedhours', e.target.value)} />
          </div>
          <div>
            <label>Effort Completed</label>
            <input type="number" step="0.25" value={draft.dagrun_pm_effortcompletedhours ?? ''} onChange={e => setNumberChange('dagrun_pm_effortcompletedhours', e.target.value)} onBlur={e => setNumberOnBlur('dagrun_pm_effortcompletedhours', e.target.value)} />
          </div>
          <div>
            <label>Effort Remaining</label>
            <input
              type="number"
              readOnly
              value={(() => {
                const est  = draft.dagrun_pm_estimatedhours ?? 0;
                const done = draft.dagrun_pm_effortcompletedhours ?? 0;
                const rem  = Math.max(0, est - done);
                return draft.dagrun_pm_estimatedhours == null ? '' : rem;
              })()}
              title="Computed: Estimated Effort − Effort Completed"
              style={{ background: 'var(--surface-2)', color: 'var(--ink-3)' }}
            />
          </div>
          <div>
            <label>% Complete</label>
            <input type="number" min={0} max={100} value={draft.dagrun_pm_percentcomplete} onChange={e => setNumberChange('dagrun_pm_percentcomplete', e.target.value)} onBlur={e => setNumberOnBlur('dagrun_pm_percentcomplete', e.target.value)} />
          </div>
          {me && task._dagrun_pm_projectid_value && !!project?.dagrun_pm_module_timetracking && (
            <div style={{ gridColumn: '1 / span 2', marginTop: 4 }}>
              <button
                type="button"
                onClick={() => setShowLogTime(true)}
                style={{
                  width: '100%',
                  background: 'transparent', color: '#0E7490',
                  border: '1px solid #0E7490', borderRadius: 6,
                  padding: '7px 12px',
                  fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#D4EEF5')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >+ Log Time</button>
            </div>
          )}
        </div>

        {/* SECTION: Custom Fields (project-defined, Settings → Custom Fields) */}
        {task._dagrun_pm_projectid_value && (
          <Section title="Custom Fields" defaultOpen={false}>
            <CustomFieldsPane pca={pca} task={task} />
          </Section>
        )}

        {/* SECTION: Recurrence */}
        <Section title="Recurrence" defaultOpen={false}>
          <RecurrencePane pca={pca} task={draft} />
        </Section>

        {/* SECTION: Tags */}
        {task._dagrun_pm_projectid_value && (
          <Section title="Tags">
            <TagSection
              pca={pca}
              pool={TASK_TAG_POOL}
              entityId={task.dagrun_pm_taskid}
              entityName={task.dagrun_pm_name}
              projectId={task._dagrun_pm_projectid_value}
              onChanged={() => { dirtyRef.current = true; }}
            />
          </Section>
        )}

        {/* SECTION: Notes */}
        <Section title="Notes" rightSlot={<PinToCardCheckbox pinKey="notes" taskId={task.dagrun_pm_taskid} userId={getMyOid(pca)} pca={pca} />}>
          <AutoSizingTextarea
            value={draft.dagrun_pm_description ?? ''}
            onChange={v => setTextDebounced('dagrun_pm_description', v)}
            onBlur={v => flushText('dagrun_pm_description', v)}
            placeholder="Add a note…"
          />
        </Section>

        {/* SECTION: Attachments (files in the project's linked SharePoint library) */}
        {task._dagrun_pm_projectid_value && (
          <Section title="Attachments" defaultOpen={false}>
            <AttachmentsPane pca={pca} taskId={task.dagrun_pm_taskid} projectId={task._dagrun_pm_projectid_value} />
          </Section>
        )}

        {/* SECTION: Checklist */}
        <Section
          title={`Checklist${task.dagrun_pm_checklistcount ? ` (${task.dagrun_pm_checklistcount})` : ''}`}
          rightSlot={<PinToCardCheckbox pinKey="checklist" taskId={task.dagrun_pm_taskid} userId={getMyOid(pca)} pca={pca} />}
        >
          <ChecklistPane pca={pca} task={task} />
        </Section>

        {/* SECTION: Subtasks */}
        <Section
          title={`Subtasks${task.dagrun_pm_subtaskcount ? ` (${task.dagrun_pm_subtaskcount})` : ''}`}
          rightSlot={<PinToCardCheckbox pinKey="subtasks" taskId={task.dagrun_pm_taskid} userId={getMyOid(pca)} pca={pca} />}
        >
          <SubtasksPane pca={pca} task={task} onOpenSubtask={setNestedTask} />
        </Section>

        {/* SECTION: Dependencies */}
        <Section title="Dependencies" defaultOpen={false}>
          <DependenciesPane pca={pca} task={task} onOpenTask={setNestedTask} onChanged={() => onSaved?.(draft)} />
        </Section>

        {/* SECTION: Approvals */}
        {task._dagrun_pm_projectid_value && me && (
          <Section title="Approvals" defaultOpen={false}>
            <EntityApprovalSection
              pca={pca}
              projectId={task._dagrun_pm_projectid_value}
              entityType={ApprovalEntityType.Task}
              entityId={task.dagrun_pm_taskid}
              entityTitle={draft.dagrun_pm_name}
              myOid={me.oid}
              canManage={me.isPM}
              activityEntityType={ActivityEntityType.Task}
              onChainState={setApprovalGate}
            />
          </Section>
        )}

        {/* SECTION: Comments */}
        <Section title={`Comments${task.dagrun_pm_commentcount ? ` (${task.dagrun_pm_commentcount})` : ''}`}>
          <CommentsPane pca={pca} task={task} projectName={project?.dagrun_pm_name ?? ''} groupId={project?.dagrun_pm_m365groupid ?? null} />
        </Section>

        {/* SECTION: Activity */}
        <Section title="Activity" defaultOpen={false}>
          <ActivityPane pca={pca} task={task} />
        </Section>
      </div>

      {nestedTask && (
        <TaskDetailDrawer task={nestedTask} pca={pca} onClose={() => setNestedTask(null)} onSaved={onSaved} onDeleted={onDeleted} />
      )}

      {showDeleteConfirm && (
        <BoardModal
          open
          title="Delete task"
          onClose={() => !deleting && setShowDeleteConfirm(false)}
          footer={<>
            <BMCancelButton onClick={() => setShowDeleteConfirm(false)} disabled={deleting} />
            <BMDangerButton onClick={handleDelete} disabled={deleting}>{deleting ? 'Deleting…' : 'Delete task'}</BMDangerButton>
          </>}
        >
          Delete <strong>{task.dagrun_pm_name}</strong>? This permanently removes the task
          {task.dagrun_pm_subtaskcount ? ' and its subtasks' : ''}. This cannot be undone.
        </BoardModal>
      )}

      {showLogTime && me && task._dagrun_pm_projectid_value && (
        <LogTimeModal
          pca={pca}
          projectId={task._dagrun_pm_projectid_value}
          isBillableDefault={true}
          meOid={me.oid}
          meName={me.displayName}
          taskId={task.dagrun_pm_taskid}
          taskName={task.dagrun_pm_name}
          onClose={() => setShowLogTime(false)}
          onCreated={() => setShowLogTime(false)}
        />
      )}
    </>
  );
}

// ── Attachments pane ──────────────────────────────────────────────────
// Files attached to a task live in the project's linked SharePoint document
// library (the same data-sovereignty pattern as the Documents tab — nothing
// is stored by Dagrun), under "Dagrun PM Task Attachments/<taskId>/".
function fmtBytes(bytes?: number): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function AttachmentsPane({ pca, taskId, projectId }: { pca: IPublicClientApplication; taskId: string; projectId: string }) {
  const [driveId, setDriveId] = useState<string | null | undefined>(undefined); // undefined = loading, null = no library
  const [folderId, setFolderId] = useState<string | null>(null);
  const [files, setFiles] = useState<DriveItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadName, setUploadName] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    setDriveId(undefined);
    getProjectLibraries(pca, projectId)
      .then(libs => { if (!cancelled) setDriveId(libs.find(l => l.dagrun_pm_driveid)?.dagrun_pm_driveid ?? null); })
      .catch(() => { if (!cancelled) setDriveId(null); });
    return () => { cancelled = true; };
  }, [pca, projectId]);

  const loadFiles = useCallback(async (drv: string) => {
    setError(null);
    try {
      const fid = await getOrCreateFolderPath(pca, drv, ['Dagrun PM Task Attachments', taskId]);
      setFolderId(fid);
      const items = (await listDriveFolder(pca, drv, fid)).filter(i => !i.folder);
      setFiles(items);
      // Keep the card's attachment count roughly in sync (best-effort).
      dvUpdate(pca, 'dagrun_pm_tasks', taskId, { dagrun_pm_attachmentcount: items.length }).catch(() => {});
    } catch {
      setError("Unable to load attachments — check that you can access the project's document library.");
      setFiles([]);
    }
  }, [pca, taskId]);

  useEffect(() => { if (driveId) loadFiles(driveId); }, [driveId, loadFiles]);

  const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !driveId || !folderId) return;
    setUploading(true); setUploadName(file.name); setError(null);
    try {
      const buf = await file.arrayBuffer();
      await uploadDriveFile(pca, driveId, folderId, file.name, buf, file.type || 'application/octet-stream');
      await loadFiles(driveId);
    } catch {
      setError('Upload failed — please try again.');
    } finally {
      setUploading(false); setUploadName(null);
    }
  };

  const download = async (item: DriveItem) => {
    if (!driveId) return;
    try {
      const url = await getDownloadUrl(pca, driveId, item.id);
      window.open(url ?? item.webUrl, '_blank', 'noopener');
    } catch {
      window.open(item.webUrl, '_blank', 'noopener');
    }
  };

  const doRemove = async (id: string) => {
    if (!driveId) return;
    setRemoving(true); setError(null);
    try { await deleteDriveItem(pca, driveId, id); setConfirmRemove(null); await loadFiles(driveId); }
    catch { setError('Could not remove the file — please try again.'); }
    finally { setRemoving(false); }
  };

  if (driveId === undefined) {
    return <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading…</div>;
  }
  if (driveId === null) {
    return (
      <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5 }}>
        Connect a document library in <strong>Settings → Documents</strong> to attach files. Attachments are
        stored in your organization's own SharePoint library — never by Dagrun.
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || !folderId}
          style={{
            background: 'transparent', color: '#0E7490', border: '1px solid #0E7490',
            borderRadius: 6, padding: '6px 12px', fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600,
            cursor: uploading || !folderId ? 'default' : 'pointer', opacity: uploading || !folderId ? 0.6 : 1,
          }}
        >{uploading ? `Uploading${uploadName ? ` ${uploadName}` : ''}…` : '+ Attach file'}</button>
        <input ref={inputRef} type="file" onChange={onFileChosen} style={{ display: 'none' }} />
      </div>

      {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 8 }}>{error}</div>}

      {files === null ? (
        <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading files…</div>
      ) : files.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--ink-4)', fontStyle: 'italic' }}>No files attached.</div>
      ) : (
        <div>
          {files.map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderTop: '0.5px solid var(--border-light)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <a href={f.webUrl} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 13, color: 'var(--ink)', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={f.name}
                >{f.name}</a>
                <div style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>{fmtBytes(f.size)}</div>
              </div>
              {confirmRemove === f.id ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Remove?</span>
                  <button type="button" onClick={() => doRemove(f.id)} disabled={removing}
                    style={{ background: 'transparent', border: 'none', color: '#DC2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Yes</button>
                  <button type="button" onClick={() => setConfirmRemove(null)} disabled={removing}
                    style={{ background: 'transparent', border: 'none', color: 'var(--ink-4)', fontSize: 12, cursor: 'pointer' }}>No</button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <button type="button" onClick={() => download(f)}
                    style={{ background: 'transparent', border: 'none', color: '#0E7490', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>Download</button>
                  <button type="button" onClick={() => setConfirmRemove(f.id)} title="Remove attachment"
                    style={{ background: 'transparent', border: 'none', color: 'var(--ink-4)', fontSize: 15, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Subtasks pane ─────────────────────────────────────────────────────
function SubtasksPane({ pca, task, onOpenSubtask }: { pca: IPublicClientApplication; task: Task; onOpenSubtask: (t: Task) => void }) {
  const [items, setItems]   = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    try { setLoading(true); setItems(await getSubtasks(pca, task.dagrun_pm_taskid)); } finally { setLoading(false); }
  }, [pca, task.dagrun_pm_taskid]);
  useEffect(() => { reload(); }, [reload]);

  const toggleDone = async (st: Task) => {
    const done = st.dagrun_pm_status === TaskStatus.Done;
    await dvUpdate(pca, 'dagrun_pm_tasks', st.dagrun_pm_taskid, { dagrun_pm_status: done ? TaskStatus.NotStarted : TaskStatus.Done, dagrun_pm_percentcomplete: done ? 0 : 100 });
    reload();
  };

  const addSubtask = async () => {
    if (!newTitle.trim() || creating) return;
    setCreating(true);
    try {
      const pid = task._dagrun_pm_projectid_value;
      if (!pid) return;
      await createSubtask(pca, pid, task.dagrun_pm_taskid, newTitle.trim(), task._dagrun_pm_wipcollectionid_value ?? undefined);
      setNewTitle(''); reload();
    } finally { setCreating(false); }
  };

  if (loading) return <div style={{ color: 'var(--ink-4)', fontSize: 13 }}>Loading…</div>;
  return (
    <div>
      {items.length === 0 && <div style={{ color: 'var(--ink-4)', fontSize: 13, marginBottom: 10 }}>No subtasks yet.</div>}
      {items.map(st => {
        const done = st.dagrun_pm_status === TaskStatus.Done;
        return (
          <div key={st.dagrun_pm_taskid} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '0.5px solid var(--border-light)' }}>
            <input type="checkbox" checked={done} onChange={() => toggleDone(st)} style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} />
            <div onClick={() => onOpenSubtask(st)} style={{ flex: 1, cursor: 'pointer', fontSize: 13, color: 'var(--ink)', textDecoration: done ? 'line-through' : 'none', opacity: done ? 0.55 : 1 }}>{st.dagrun_pm_name}</div>
            <span className="pill" style={{ background: `${PRIORITY_COLOR[st.dagrun_pm_priority] ?? '#9198A8'}22`, color: PRIORITY_COLOR[st.dagrun_pm_priority] ?? 'var(--ink-3)' }}>{PRIORITY_LABEL[st.dagrun_pm_priority] ?? '—'}</span>
            {st.dagrun_pm_assigneename && <Avatar name={st.dagrun_pm_assigneename} />}
          </div>
        );
      })}
      <input placeholder="+ Add subtask…" value={newTitle} onChange={e => setNewTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addSubtask(); if (e.key === 'Escape') setNewTitle(''); }} disabled={creating} style={{ marginTop: 10 }} />
    </div>
  );
}

// ── Checklist pane ────────────────────────────────────────────────────
function ChecklistPane({ pca, task }: { pca: IPublicClientApplication; task: Task }) {
  const [items, setItems]     = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState('');
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    try { setLoading(true); setItems(await getChecklistItems(pca, task.dagrun_pm_taskid)); } finally { setLoading(false); }
  }, [pca, task.dagrun_pm_taskid]);
  useEffect(() => { reload(); }, [reload]);

  const total = items.length;
  const done = items.filter(i => i.dagrun_pm_iscompleted).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const toggle = async (item: ChecklistItem) => {
    const next = !item.dagrun_pm_iscompleted;
    setItems(a => a.map(x => x.dagrun_pm_checklistitemid === item.dagrun_pm_checklistitemid ? { ...x, dagrun_pm_iscompleted: next } : x));
    try { await updateChecklistItem(pca, item.dagrun_pm_checklistitemid, { dagrun_pm_iscompleted: next, ...(next ? { dagrun_pm_completedon: new Date().toISOString() } : { dagrun_pm_completedon: null }) }); } catch { reload(); }
  };

  const renameItem = async (item: ChecklistItem, text: string) => {
    if (text === item.dagrun_pm_name || !text.trim()) return;
    try { await updateChecklistItem(pca, item.dagrun_pm_checklistitemid, { dagrun_pm_name: text }); setItems(a => a.map(x => x.dagrun_pm_checklistitemid === item.dagrun_pm_checklistitemid ? { ...x, dagrun_pm_name: text } : x)); } catch { reload(); }
  };

  const removeItem = async (item: ChecklistItem) => {
    setItems(a => a.filter(x => x.dagrun_pm_checklistitemid !== item.dagrun_pm_checklistitemid));
    try { await deleteChecklistItem(pca, item.dagrun_pm_checklistitemid); } catch { reload(); }
  };

  const addItem = async () => {
    if (!newText.trim() || creating) return;
    setCreating(true);
    try { const so = (items.reduce((m, x) => Math.max(m, x.dagrun_pm_sortorder ?? 0), 0)) + 1; await createChecklistItem(pca, task.dagrun_pm_taskid, newText.trim(), so); setNewText(''); reload(); } finally { setCreating(false); }
  };

  if (loading) return <div style={{ color: 'var(--ink-4)', fontSize: 13 }}>Loading…</div>;
  return (
    <div>
      {total > 0 && (
        <>
          <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 4, fontFamily: 'var(--font-mono)' }}>{done} of {total} complete</div>
          <div style={{ background: 'var(--border-light)', borderRadius: 2, height: 3, overflow: 'hidden', marginBottom: 10 }}>
            <div style={{ background: 'var(--brand)', height: '100%', width: `${pct}%`, transition: 'width 0.2s' }} />
          </div>
        </>
      )}
      {items.length === 0 && <div style={{ color: 'var(--ink-4)', fontSize: 13, marginBottom: 10 }}>No checklist items yet.</div>}
      {items.map(item => <ChecklistRow key={item.dagrun_pm_checklistitemid} item={item} onToggle={() => toggle(item)} onRename={text => renameItem(item, text)} onDelete={() => removeItem(item)} />)}
      <input placeholder="+ Add checklist item…" value={newText} onChange={e => setNewText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addItem(); if (e.key === 'Escape') setNewText(''); }} disabled={creating} style={{ marginTop: 10 }} />
    </div>
  );
}

function ChecklistRow({ item, onToggle, onRename, onDelete }: { item: ChecklistItem; onToggle: () => void; onRename: (text: string) => void; onDelete: () => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.dagrun_pm_name);
  useEffect(() => { setText(item.dagrun_pm_name); }, [item.dagrun_pm_name]);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', borderBottom: '0.5px solid var(--border-light)' }}>
      <input type="checkbox" checked={item.dagrun_pm_iscompleted} onChange={onToggle} style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} />
      {editing ? (
        <input autoFocus value={text} onChange={e => setText(e.target.value)} onBlur={() => { setEditing(false); onRename(text); }} onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') { setText(item.dagrun_pm_name); setEditing(false); } }} style={{ flex: 1, fontSize: 13, padding: '3px 6px' }} />
      ) : (
        <div onClick={() => setEditing(true)} style={{ flex: 1, fontSize: 13, color: 'var(--ink)', textDecoration: item.dagrun_pm_iscompleted ? 'line-through' : 'none', opacity: item.dagrun_pm_iscompleted ? 0.55 : 1, cursor: 'text', minHeight: 20 }}>{item.dagrun_pm_name}</div>
      )}
      <button type="button" onClick={onDelete} aria-label="Delete" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', fontSize: 14, padding: 2, lineHeight: 1 }} onMouseEnter={e => (e.currentTarget.style.color = 'var(--danger)')} onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-4)')}>×</button>
    </div>
  );
}

// ── Comments pane ─────────────────────────────────────────────────────
interface MentionCandidate { id: string; name: string; email?: string | null }

function renderCommentBody(body: string): React.ReactNode {
  // Highlight @Name spans (case-sensitive — the picker inserts them
  // with the original display name). Conservative regex: @ followed by
  // a word + optional space-joined word(s), up to 3 tokens.
  const out: React.ReactNode[] = [];
  const re = /@([A-Z][\p{L}'-]+(?:\s[A-Z][\p{L}'-]+){0,2})/gu;
  let lastIdx = 0; let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    if (m.index > lastIdx) out.push(body.slice(lastIdx, m.index));
    out.push(<span key={m.index} style={{ color: 'var(--brand)', fontWeight: 600 }}>@{m[1]}</span>);
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < body.length) out.push(body.slice(lastIdx));
  return out;
}

function CommentsPane({ pca, task, projectName, groupId }: { pca: IPublicClientApplication; task: Task; projectName: string; groupId?: string | null }) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [meId, setMeId] = useState('');
  const [meName, setMeName] = useState('');
  const taRef = useRef<HTMLTextAreaElement>(null);

  // @mention picker state.
  const [members, setMembers] = useState<MentionCandidate[]>([]);
  const [graphResults, setGraphResults] = useState<MentionCandidate[]>([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionStart, setMentionStart] = useState<number>(-1);
  const [mentionIdx, setMentionIdx] = useState(0);

  // Resolved-mention list, populated by insertMention as the user picks
  // candidates from the picker. Stored in a ref (not state) because the
  // submit handler is the only consumer and re-rendering the textarea
  // on every pick would interrupt typing. The previous run parsed the
  // committed body string against `members` at submit time — that
  // missed any mention resolved through the Graph fallback (Graph
  // results never made it into `members`), so dagrun_pm_mention stayed
  // empty for any non-project-member @mention. Reading from the ref
  // captures the actual user choice regardless of source.
  const mentionsRef = useRef<MentionCandidate[]>([]);

  useEffect(() => {
    getAccount(pca).then(a => { if (!a) return; setMeId(a.localAccountId ?? a.homeAccountId?.split('.')[0] ?? ''); setMeName(a.name ?? a.username ?? 'Me'); }).catch(() => {});
  }, [pca]);

  const reload = useCallback(async () => {
    try { setLoading(true); setComments(await getTaskComments(pca, task.dagrun_pm_taskid)); } finally { setLoading(false); }
  }, [pca, task.dagrun_pm_taskid]);
  useEffect(() => { reload(); }, [reload]);

  // Build mention candidates. When the project is linked to an M365 Group,
  // the Group's members are the primary roster; otherwise fall back to the
  // dagrun_pm_projectmembers list. The assignee is always included, and the
  // tenant Graph search (below) is the fallback when nothing matches here.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const candidates = new Map<string, MentionCandidate>();
      const pid = task._dagrun_pm_projectid_value;
      if (groupId) {
        try {
          const { getGroupMembers } = await import('../services/groupService');
          const gm = await getGroupMembers(pca, groupId);
          gm.forEach(m => {
            if (m.id && m.displayName) {
              candidates.set(m.id, { id: m.id, name: m.displayName, email: m.mail ?? m.userPrincipalName });
            }
          });
        } catch { /* non-fatal — fall through to project members */ }
      }
      if (candidates.size === 0 && pid) {
        try {
          const { getProjectMembers } = await import('../services/projectMemberService');
          const ms = await getProjectMembers(pca, pid);
          ms.forEach(m => {
            if (m.dagrun_pm_userid && m.dagrun_pm_username) {
              candidates.set(m.dagrun_pm_userid, { id: m.dagrun_pm_userid, name: m.dagrun_pm_username });
            }
          });
        } catch { /* non-fatal */ }
      }
      if (task.dagrun_pm_assigneeid && task.dagrun_pm_assigneename) {
        candidates.set(task.dagrun_pm_assigneeid, { id: task.dagrun_pm_assigneeid, name: task.dagrun_pm_assigneename });
      }
      if (cancelled) return;
      setMembers([...candidates.values()].sort((a, b) => a.name.localeCompare(b.name)));
    })();
    return () => { cancelled = true; };
  }, [pca, groupId, task._dagrun_pm_projectid_value, task.dagrun_pm_assigneeid, task.dagrun_pm_assigneename]);

  // Graph fallback search — when the project has no members in
  // dagrun_pm_projectmembers (or no match in the local list), search the
  // tenant via Graph after the user types ≥2 chars after @. Same pattern
  // PeoplePicker uses. Debounced 300ms.
  useEffect(() => {
    if (!mentionOpen) { setGraphResults([]); return; }
    const q = mentionFilter.trim();
    if (q.length < 2) { setGraphResults([]); return; }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        const encoded = encodeURIComponent(`"displayName:${q}"`);
        const data = await graphGet<{ value: { id: string; displayName: string; mail: string | null; userPrincipalName: string | null }[] }>(
          pca,
          `/users?$search=${encoded}&$select=id,displayName,mail,userPrincipalName&$top=8&$orderby=displayName`,
          { ConsistencyLevel: 'eventual' },
        );
        if (cancelled) return;
        setGraphResults((data.value ?? []).map(u => ({ id: u.id, name: u.displayName, email: u.mail ?? u.userPrincipalName })));
      } catch {
        if (!cancelled) setGraphResults([]);
      }
    }, 300);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [mentionOpen, mentionFilter, pca]);

  // Merge local members with Graph results, dedupe by id, then filter.
  const merged = (() => {
    const byId = new Map<string, MentionCandidate>();
    members.forEach(m => byId.set(m.id, m));
    graphResults.forEach(m => { if (!byId.has(m.id)) byId.set(m.id, m); });
    return [...byId.values()];
  })();
  const filtered = mentionOpen
    ? merged.filter(m => m.name.toLowerCase().includes(mentionFilter.toLowerCase())).slice(0, 6)
    : [];

  const onBodyChange = (v: string) => {
    setBody(v);
    // If the user backspaced over a previously-inserted "@DisplayName"
    // span, drop that resolved mention from the ref so we don't notify
    // someone whose tag no longer appears in the comment.
    if (mentionsRef.current.length > 0) {
      mentionsRef.current = mentionsRef.current.filter(m => v.includes(`@${m.name}`));
    }
    const caret = taRef.current?.selectionStart ?? v.length;
    // Look back from caret for an @ that starts a token.
    const upto = v.slice(0, caret);
    const at = upto.lastIndexOf('@');
    if (at < 0) { setMentionOpen(false); return; }
    const after = upto.slice(at + 1);
    // Token is "word characters" only — close picker if a whitespace appears after @.
    if (/\s/.test(after)) { setMentionOpen(false); return; }
    // Open only when @ is at start or follows a space (i.e. a word boundary).
    const before = at === 0 ? ' ' : upto.charAt(at - 1);
    if (!/\s/.test(before)) { setMentionOpen(false); return; }
    setMentionOpen(true); setMentionStart(at); setMentionFilter(after); setMentionIdx(0);
  };

  const insertMention = (cand: MentionCandidate) => {
    const ta = taRef.current;
    const caret = ta?.selectionStart ?? body.length;
    const before = body.slice(0, mentionStart);
    const after  = body.slice(caret);
    const insert = `@${cand.name} `;
    const next = before + insert + after;
    setBody(next);
    setMentionOpen(false);
    // Record the resolved mention. Dedupe by id so the same person
    // mentioned twice in one comment counts once.
    if (!mentionsRef.current.some(m => m.id === cand.id)) {
      mentionsRef.current = [...mentionsRef.current, cand];
    }
    setTimeout(() => {
      if (ta) {
        const pos = before.length + insert.length;
        ta.focus(); ta.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const submit = async () => {
    const t = body.trim(); if (!t || posting) return;
    setPosting(true);
    try {
      const commentId = await createTaskComment(
        pca, task.dagrun_pm_taskid, meId, meName, t, task._dagrun_pm_projectid_value ?? undefined,
      );
      // Snapshot the resolved-mention list at submit time, then clear
      // the ref before any await yields control. Reset must happen
      // BEFORE the body clear so a fast double-click can't reuse stale
      // mentions on the next comment.
      const resolvedMentions = mentionsRef.current;
      mentionsRef.current = [];
      // Instrumentation (kept intentionally) — verify the comment saved and
      // which AAD OIDs were resolved for notification.
      console.log('MENTION: comment saved, mentionedUserIds:', resolvedMentions.map(m => m.id));
      setBody(''); reload();

      const projectId = task._dagrun_pm_projectid_value;
      if (resolvedMentions.length > 0 && projectId) {
        // Fire-and-forget per spec — comment write must not be blocked
        // on notification delivery. Failures are captured to each
        // mention row's notificationerror column AND console.error'd.
        sendMentionNotification(pca, {
          commentId,
          commentBody: t,
          taskId: task.dagrun_pm_taskid,
          taskName: task.dagrun_pm_name,
          projectId,
          projectName,
          entityType: 'Task',
          entityId: task.dagrun_pm_taskid,
          mentionedUsers: resolvedMentions.map(m => ({ userId: m.id, userName: m.name })),
        }).catch(err => console.error('sendMentionNotification failed:', err));
      }
    } finally {
      setPosting(false);
    }
  };

  return (
    <div>
      {loading && <div style={{ color: 'var(--ink-4)', fontSize: 13 }}>Loading…</div>}
      {!loading && comments.length === 0 && <div style={{ color: 'var(--ink-4)', fontSize: 13, marginBottom: 10 }}>No comments yet.</div>}
      {comments.map(c => (
        <div key={c.dagrun_pm_commentid} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '0.5px solid var(--border-light)' }}>
          <Avatar name={c.dagrun_pm_authorname} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{c.dagrun_pm_authorname ?? 'Unknown'}</span>
              <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>{relativeTime(c.createdon)}</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.5, marginTop: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {renderCommentBody(c.dagrun_pm_body)}
            </div>
          </div>
        </div>
      ))}
      <div style={{ borderTop: '0.5px solid var(--border-light)', paddingTop: 10, marginTop: 8 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Avatar name={meName} />
          <div style={{ flex: 1, position: 'relative' }}>
            <textarea
              ref={taRef}
              value={body}
              onChange={e => onBodyChange(e.target.value)}
              onKeyDown={e => {
                if (mentionOpen) {
                  if (e.key === 'Escape')    { e.preventDefault(); setMentionOpen(false); return; }
                  if (filtered.length > 0) {
                    if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx(i => Math.min(filtered.length - 1, i + 1)); return; }
                    if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionIdx(i => Math.max(0, i - 1)); return; }
                    if (e.key === 'Enter')     { e.preventDefault(); insertMention(filtered[mentionIdx]); return; }
                    if (e.key === 'Tab')       { e.preventDefault(); insertMention(filtered[mentionIdx]); return; }
                  }
                }
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); }
              }}
              placeholder="Add a comment — type @ to mention…"
              rows={1}
              style={{ minHeight: 36, maxHeight: 120, fontSize: 13, width: '100%' }}
            />
            {/* Picker always renders while mentionOpen — even with zero
                results — so the user gets immediate feedback that the @
                trigger was detected. The empty state shows "No members
                found" rather than silently rendering nothing. */}
            {mentionOpen && (
              <div style={{
                position: 'absolute', left: 0, bottom: '100%', marginBottom: 4,
                background: '#fff', border: '1px solid var(--border)',
                borderRadius: 6, boxShadow: '0 4px 16px rgba(15,17,23,0.12)',
                minWidth: 220, maxWidth: 320, zIndex: 50, padding: 4,
              }}>
                {filtered.length === 0 ? (
                  <div style={{
                    padding: '8px 12px',
                    fontSize: 12, color: 'var(--ink-4)',
                    fontStyle: 'italic',
                    fontFamily: 'var(--font-ui)',
                  }}>
                    {mentionFilter.length < 2
                      ? 'Type a name to search…'
                      : 'No members found'}
                  </div>
                ) : filtered.map((cand, i) => (
                  <button
                    key={cand.id}
                    type="button"
                    onMouseDown={e => { e.preventDefault(); insertMention(cand); }}
                    style={{
                      width: '100%', textAlign: 'left',
                      background: i === mentionIdx ? 'var(--brand-mist)' : 'transparent',
                      color: i === mentionIdx ? 'var(--brand)' : 'var(--ink)',
                      border: 'none', borderRadius: 4,
                      padding: '6px 10px', fontSize: 13,
                      cursor: 'pointer', fontFamily: 'var(--font-ui)',
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}
                    onMouseEnter={() => setMentionIdx(i)}
                  >
                    <Avatar name={cand.name} size={20} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cand.name}</span>
                      {cand.email && (
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cand.email}</span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
              <button className="btn-primary" onClick={submit} disabled={posting || !body.trim() || !meId}>{posting ? 'Posting…' : 'Post'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Linked Tasks pane (Dependencies) ──────────────────────────────────
function DependenciesPane({ pca, task, onOpenTask, onChanged }: { pca: IPublicClientApplication; task: Task; onOpenTask?: (t: Task) => void; onChanged?: () => void }) {
  const [deps, setDeps]       = useState<Dependency[]>([]);
  const [projectTasks, setProjectTasks] = useState<Task[]>([]);
  const [collections, setCollections]   = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding]   = useState(false);
  const [direction, setDirection] = useState<'blocked_by' | 'blocks'>('blocked_by');
  const [targetTaskId, setTargetTaskId] = useState<string>('');
  const [depType, setDepType] = useState<number>(DependencyType.FS);
  const [lagDays, setLagDays] = useState<number>(0);
  const [expandedCol, setExpandedCol] = useState<string | null>(null);
  const [showCompleted, setShowCompleted] = useState<Record<string, boolean>>({});
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [search, setSearch]   = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const pid = task._dagrun_pm_projectid_value;
      const [d, t, c] = await Promise.all([
        getTaskDependencies(pca, task.dagrun_pm_taskid),
        pid ? getProjectTasks(pca, pid) : Promise.resolve([] as Task[]),
        pid ? getProjectCollections(pca, pid) : Promise.resolve([] as Collection[]),
      ]);
      setDeps(d); setProjectTasks(t); setCollections(c);
    } finally {
      setLoading(false);
    }
  }, [pca, task.dagrun_pm_taskid, task._dagrun_pm_projectid_value]);
  useEffect(() => { reload(); }, [reload]);

  const taskById = new Map<string, Task>();
  projectTasks.forEach(t => taskById.set(t.dagrun_pm_taskid, t));

  // Unified list — every dependency touching this task, with a direction flag.
  interface LinkedRow { dep: Dependency; otherId: string; direction: 'before' | 'after' }
  const linked: LinkedRow[] = deps.map(d => {
    if (d._dagrun_pm_successortaskid_value === task.dagrun_pm_taskid) {
      return { dep: d, otherId: d._dagrun_pm_predecessortaskid_value, direction: 'before' };
    }
    return { dep: d, otherId: d._dagrun_pm_successortaskid_value, direction: 'after' };
  });

  const linkedOtherIds = new Set<string>(linked.map(l => l.otherId));

  const submit = async () => {
    if (!targetTaskId || !task._dagrun_pm_projectid_value) return;
    setSaving(true); setError(null);
    try {
      await createDependency(pca, {
        projectId: task._dagrun_pm_projectid_value,
        predecessorTaskId: direction === 'blocked_by' ? targetTaskId : task.dagrun_pm_taskid,
        successorTaskId:   direction === 'blocked_by' ? task.dagrun_pm_taskid : targetTaskId,
        dependencyType: depType,
        lagDays: lagDays > 0 ? lagDays : null,
      });
      setAdding(false); setTargetTaskId(''); setDepType(DependencyType.FS); setLagDays(0); setExpandedCol(null); setSearch('');
      await reload();
      onChanged?.();  // refresh the Gantt arrows if it's the active view
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  // Removal is two-step: the × arms an inline confirm on the row; only the
  // explicit Remove click deletes.
  const removeDep = async (dep: Dependency) => {
    setConfirmRemoveId(null);
    setDeps(a => a.filter(d => d.dagrun_pm_dependencyid !== dep.dagrun_pm_dependencyid));
    try { await deleteDependency(pca, dep.dagrun_pm_dependencyid); onChanged?.(); }
    catch { reload(); }
  };

  if (loading) return <div style={{ color: 'var(--ink-4)', fontSize: 13 }}>Loading…</div>;

  const renderLinkedRow = (row: LinkedRow) => {
    const t = taskById.get(row.otherId);
    const name = t?.dagrun_pm_name ?? '(unknown task)';
    const lag  = row.dep.dagrun_pm_lagdays ?? 0;
    const typeShort = DEPENDENCY_TYPE_SHORT[row.dep.dagrun_pm_dependencytype] ?? '—';
    const typeLabel = DEPENDENCY_TYPE_LABEL[row.dep.dagrun_pm_dependencytype] ?? '—';
    return (
      <div key={row.dep.dagrun_pm_dependencyid}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '0.5px solid var(--border-light)' }}>
        <button
          type="button"
          onClick={() => t && onOpenTask?.(t)}
          disabled={!t}
          style={{
            flex: 1,
            background: 'transparent', border: 'none', padding: 0,
            textAlign: 'left', cursor: t ? 'pointer' : 'default',
            fontSize: 13, color: t ? 'var(--brand)' : 'var(--ink-4)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            fontFamily: 'var(--font-ui)',
          }}
        >{name}</button>
        <span title={typeLabel} style={{
          fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600,
          background: '#D4EEF5', color: '#0A5568',
          padding: '2px 6px', borderRadius: 3, flexShrink: 0,
          letterSpacing: '0.04em', whiteSpace: 'nowrap',
        }}>{typeShort}</span>
        {lag > 0 && (
          <span style={{ fontSize: 10, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
            +{lag}d
          </span>
        )}
        {confirmRemoveId === row.dep.dagrun_pm_dependencyid ? (
          <span style={{ display: 'inline-flex', gap: 4, flexShrink: 0 }}>
            <button type="button" onClick={() => removeDep(row.dep)}
              style={{ background: 'var(--danger)', color: '#fff', border: 'none', padding: '2px 8px', fontSize: 10, fontWeight: 600, borderRadius: 4, cursor: 'pointer' }}
            >Remove</button>
            <button type="button" onClick={() => setConfirmRemoveId(null)}
              style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--ink-3)', padding: '2px 8px', fontSize: 10, borderRadius: 4, cursor: 'pointer' }}
            >Cancel</button>
          </span>
        ) : (
          <button type="button" onClick={() => setConfirmRemoveId(row.dep.dagrun_pm_dependencyid)} aria-label="Remove"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', fontSize: 14, lineHeight: 1, flexShrink: 0 }}
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--danger)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-4)')}
          >×</button>
        )}
      </div>
    );
  };

  const predecessors = linked.filter(l => l.direction === 'before'); // this task depends on these
  const successors   = linked.filter(l => l.direction === 'after');  // these depend on this task

  return (
    <div>
      {linked.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--ink-4)', marginBottom: 8 }}>No dependencies</div>
      )}

      {predecessors.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
            Predecessors · this task depends on
          </div>
          {predecessors.map(renderLinkedRow)}
        </div>
      )}
      {successors.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
            Successors · depend on this task
          </div>
          {successors.map(renderLinkedRow)}
        </div>
      )}

      {!adding ? (
        <button type="button" onClick={() => setAdding(true)}
          style={{
            marginTop: 12,
            background: 'transparent', color: 'var(--brand)',
            border: '1px solid var(--brand)', borderRadius: 6,
            padding: '6px 12px',
            fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600,
            cursor: 'pointer',
          }}
        >+ Add Dependency</button>
      ) : (
        <div style={{
          marginTop: 12, padding: 10,
          background: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 6,
        }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <button type="button" onClick={() => setDirection('blocked_by')}
              style={{ flex: 1, padding: '4px 8px', fontSize: 11, fontWeight: 500, background: direction === 'blocked_by' ? 'var(--brand)' : '#fff', color: direction === 'blocked_by' ? '#fff' : 'var(--ink-2)', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer' }}
            >Link as predecessor</button>
            <button type="button" onClick={() => setDirection('blocks')}
              style={{ flex: 1, padding: '4px 8px', fontSize: 11, fontWeight: 500, background: direction === 'blocks' ? 'var(--brand)' : '#fff', color: direction === 'blocks' ? '#fff' : 'var(--ink-2)', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer' }}
            >Link as successor</button>
          </div>

          <label style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 4, display: 'block' }}>Pick a task</label>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search tasks in this project…"
            style={{ width: '100%', fontSize: 12, marginBottom: 6 }}
          />
          <div style={{
            border: '1px solid var(--border)',
            borderRadius: 6,
            background: '#fff',
            maxHeight: 240, overflowY: 'auto',
            marginBottom: 8,
          }}>
            {search.trim() ? (
              // Flat search results replace the collection tree while typing.
              (() => {
                const q = search.trim().toLowerCase();
                const hits = projectTasks.filter(t =>
                  t.dagrun_pm_taskid !== task.dagrun_pm_taskid
                  && !linkedOtherIds.has(t.dagrun_pm_taskid)
                  && t.dagrun_pm_name.toLowerCase().includes(q));
                if (hits.length === 0) {
                  return <div style={{ padding: 10, fontSize: 12, color: 'var(--ink-4)' }}>No matching tasks.</div>;
                }
                return hits.slice(0, 30).map(t => {
                  const selected = targetTaskId === t.dagrun_pm_taskid;
                  return (
                    <div key={t.dagrun_pm_taskid}
                      onClick={() => setTargetTaskId(t.dagrun_pm_taskid)}
                      style={{
                        padding: '7px 10px', fontSize: 13, cursor: 'pointer',
                        background: selected ? 'var(--brand-mist)' : 'transparent',
                        color: selected ? 'var(--brand)' : 'var(--ink)',
                        fontWeight: selected ? 600 : 400,
                        borderBottom: '0.5px solid var(--border-light)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                      onMouseEnter={e => !selected && (e.currentTarget.style.background = 'var(--surface-2)')}
                      onMouseLeave={e => !selected && (e.currentTarget.style.background = 'transparent')}
                    >{t.dagrun_pm_name}</div>
                  );
                });
              })()
            ) : (
              <CollectionPicker
                collections={collections}
                projectTasks={projectTasks}
                excludeTaskId={task.dagrun_pm_taskid}
                excludeIds={linkedOtherIds}
                expandedCol={expandedCol}
                setExpandedCol={setExpandedCol}
                showCompleted={showCompleted}
                setShowCompleted={setShowCompleted}
                selectedId={targetTaskId}
                onSelect={setTargetTaskId}
              />
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, color: 'var(--ink-3)' }}>Type</label>
              <select value={depType} onChange={e => setDepType(Number(e.target.value))} style={{ width: '100%', fontSize: 12 }}>
                <option value={DependencyType.FS}>Finish-to-Start</option>
                <option value={DependencyType.SS}>Start-to-Start</option>
                <option value={DependencyType.FF}>Finish-to-Finish</option>
                <option value={DependencyType.SF}>Start-to-Finish</option>
              </select>
            </div>
            <div style={{ width: 90 }}>
              <label style={{ fontSize: 10, color: 'var(--ink-3)' }}>Lag (days)</label>
              <input type="number" min={0} value={lagDays}
                onChange={e => setLagDays(Math.max(0, Number(e.target.value) || 0))}
                style={{ width: '100%', fontSize: 12 }} />
            </div>
          </div>

          {error && <div style={{ fontSize: 11, color: 'var(--danger)', marginBottom: 6 }}>{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            <button type="button" onClick={() => { setAdding(false); setTargetTaskId(''); setLagDays(0); setError(null); setSearch(''); }} disabled={saving}
              style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--ink-3)', padding: '4px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer' }}
            >Cancel</button>
            <button type="button" onClick={submit} disabled={saving || !targetTaskId}
              style={{ background: 'var(--brand)', border: 'none', color: '#fff', padding: '4px 10px', fontSize: 12, fontWeight: 600, borderRadius: 4, cursor: saving || !targetTaskId ? 'not-allowed' : 'pointer', opacity: saving || !targetTaskId ? 0.6 : 1 }}
            >{saving ? 'Linking…' : 'Link'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Collection-hierarchy task picker (used by the link-a-task flow) ────
function CollectionPicker({
  collections, projectTasks, excludeTaskId, excludeIds,
  expandedCol, setExpandedCol, showCompleted, setShowCompleted,
  selectedId, onSelect,
}: {
  collections: Collection[];
  projectTasks: Task[];
  excludeTaskId: string;
  excludeIds: Set<string>;
  expandedCol: string | null;
  setExpandedCol: (id: string | null) => void;
  showCompleted: Record<string, boolean>;
  setShowCompleted: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const byCollection = new Map<string, Task[]>();
  collections.forEach(c => byCollection.set(c.dagrun_pm_collectionid, []));
  const UNASSIGNED = '__unassigned';
  byCollection.set(UNASSIGNED, []);
  projectTasks.forEach(t => {
    if (t.dagrun_pm_taskid === excludeTaskId || excludeIds.has(t.dagrun_pm_taskid)) return;
    const colId = t._dagrun_pm_wipcollectionid_value ?? UNASSIGNED;
    const arr = byCollection.get(colId) ?? byCollection.get(UNASSIGNED)!;
    arr.push(t);
  });

  const renderCollection = (colId: string, name: string, color: string | null) => {
    const items = byCollection.get(colId) ?? [];
    if (items.length === 0) return null;
    const active    = items.filter(t => t.dagrun_pm_status !== TaskStatus.Done);
    const completed = items.filter(t => t.dagrun_pm_status === TaskStatus.Done);
    const open = expandedCol === colId;
    const expanded = open;
    const completedOpen = !!showCompleted[colId];
    const headerFrom = color || '#0E7490';
    const headerTo   = color ? darkenHex(color, 15) : '#0A5568';
    return (
      <div key={colId} style={{ borderBottom: '1px solid var(--border-light)' }}>
        <button
          type="button"
          onClick={() => setExpandedCol(open ? null : colId)}
          style={{
            width: '100%', textAlign: 'left', border: 'none',
            padding: '7px 10px',
            background: `linear-gradient(135deg, ${headerFrom}, ${headerTo})`,
            color: '#fff',
            fontFamily: 'var(--font-ui)',
            fontSize: 11, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 9 }}>{expanded ? '▾' : '▸'}</span>
            <span>{name}</span>
          </span>
          <span style={{ fontSize: 9, opacity: 0.8, fontFamily: 'var(--font-mono)' }}>{items.length}</span>
        </button>
        {expanded && (
          <div style={{ padding: '4px 0' }}>
            {active.length === 0 && (
              <div style={{ padding: '6px 16px', fontSize: 12, color: 'var(--ink-4)', fontStyle: 'italic' }}>No active tasks.</div>
            )}
            {active.map(t => renderTaskRow(t, selectedId, onSelect))}
            {completed.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setShowCompleted(prev => ({ ...prev, [colId]: !prev[colId] }))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '5px 16px',
                    border: 'none', background: 'transparent',
                    fontFamily: 'var(--font-ui)', fontSize: 10, fontWeight: 500,
                    color: 'var(--ink-4)',
                    cursor: 'pointer', width: '100%', textAlign: 'left',
                  }}
                >
                  <span style={{ fontSize: 8 }}>{completedOpen ? '▾' : '▸'}</span>
                  {completed.length} completed
                </button>
                {completedOpen && completed.map(t => renderTaskRow(t, selectedId, onSelect, true))}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {collections.map(c => renderCollection(c.dagrun_pm_collectionid, c.dagrun_pm_name, c.dagrun_pm_color))}
      {(byCollection.get(UNASSIGNED)?.length ?? 0) > 0 && renderCollection(UNASSIGNED, 'Unassigned', null)}
    </>
  );
}

function renderTaskRow(t: Task, selectedId: string, onSelect: (id: string) => void, done = false) {
  const selected = selectedId === t.dagrun_pm_taskid;
  return (
    <div
      key={t.dagrun_pm_taskid}
      onClick={() => onSelect(t.dagrun_pm_taskid)}
      style={{
        padding: '6px 16px',
        fontSize: 13,
        color: selected ? 'var(--brand)' : (done ? 'var(--ink-4)' : 'var(--ink)'),
        background: selected ? 'var(--brand-mist)' : 'transparent',
        cursor: 'pointer',
        fontWeight: selected ? 500 : 400,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        textDecoration: done ? 'line-through' : 'none',
        opacity: done ? 0.75 : 1,
      }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.background = 'var(--surface-2)'; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
    >
      {t.dagrun_pm_name}
    </div>
  );
}

// ── Activity pane ──────────────────────────────────────────────────
// Chronological feed of dagrun_pm_activitylog entries for this task.
// Lazy-loads on first open of the Activity section. Empty state when
// no entries. For entries missing actorname, fires a Graph /users/{id}
// lookup in the background and patches the row in place.
function ActivityPane({ pca, task }: { pca: IPublicClientApplication; task: Task }) {
  const [entries, setEntries] = useState<ActivityLogEntry[] | null>(null);
  const [resolvedNames, setResolvedNames] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    getEntityActivity(pca, ActivityEntityType.Task, task.dagrun_pm_taskid)
      .then(list => { if (!cancelled) setEntries(list); })
      .catch(() => { if (!cancelled) setEntries([]); });
    return () => { cancelled = true; };
  }, [pca, task.dagrun_pm_taskid]);

  // Backfill actor display names via Graph when missing. Fire-and-forget.
  useEffect(() => {
    if (!entries || entries.length === 0) return;
    const missing = entries
      .filter(e => e.dagrun_pm_actorid && !resolvedNames[e.dagrun_pm_actorid])
      .filter(e => {
        // Skip if the row already has an actorname field populated.
        const actorName = (e as unknown as { dagrun_pm_actorname?: string | null }).dagrun_pm_actorname;
        return !actorName;
      })
      .map(e => e.dagrun_pm_actorid as string);
    const unique = Array.from(new Set(missing));
    if (unique.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const oid of unique) {
        if (cancelled) return;
        try {
          const u = await graphGet<{ displayName?: string }>(pca, `/users/${oid}?$select=displayName`);
          if (u.displayName && !cancelled) {
            setResolvedNames(prev => ({ ...prev, [oid]: u.displayName as string }));
          }
        } catch { /* skip */ }
      }
    })();
    return () => { cancelled = true; };
  }, [entries, resolvedNames, pca]);

  if (entries === null) {
    return <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Loading activity…</div>;
  }
  if (entries.length === 0) {
    return (
      <div style={{ fontSize: 13, color: 'var(--ink-4)', padding: '10px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 16 }}>📋</span>
        <span>No activity yet.</span>
      </div>
    );
  }
  return (
    <div>
      {entries.map(e => {
        const actorRaw = (e as unknown as { dagrun_pm_actorname?: string | null }).dagrun_pm_actorname;
        const actorName = actorRaw ?? (e.dagrun_pm_actorid ? resolvedNames[e.dagrun_pm_actorid] : null) ?? 'Someone';
        return (
          <div key={e.dagrun_pm_activitylogid}
            style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '0.5px solid var(--border-light)' }}>
            <ActivityAvatar name={actorName} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.4 }}>
                <strong>{actorName}</strong>{' '}
                <span style={{ color: 'var(--ink-3)' }}>{formatAction(e.dagrun_pm_actiontype)}</span>
                {e.dagrun_pm_actiondescription && (
                  <span style={{ color: 'var(--ink-3)' }}> — {e.dagrun_pm_actiondescription}</span>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                {relativeTime(e.createdon)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatAction(action?: string | null): string {
  if (!action) return 'did something';
  switch (action) {
    case 'Created': return 'created this task';
    case 'StatusChanged': return 'changed status';
    case 'Assigned': return 'changed assignee';
    case 'Unassigned': return 'removed assignee';
    case 'PriorityChanged': return 'changed priority';
    case 'DueDateChanged': return 'changed due date';
    case 'PercentCompleteChanged': return 'updated % complete';
    case 'ChecklistItemCompleted': return 'completed a checklist item';
    case 'Commented': return 'added a comment';
    default: return action;
  }
}

function ActivityAvatar({ name }: { name: string }) {
  const AVATAR_COLORS = ['#0E7490', '#7C3AED', '#B45309', '#065F46', '#9D174D'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  const color = AVATAR_COLORS[h % AVATAR_COLORS.length];
  const initials = name.split(/\s+/).slice(0, 2).map(s => s[0] ?? '').join('').toUpperCase() || '?';
  return (
    <div style={{
      width: 22, height: 22, borderRadius: '50%',
      background: color, color: '#fff',
      fontSize: 10, fontWeight: 600,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>{initials}</div>
  );
}
